import React from 'react';
import styled from 'styled-components';
import { PhoneNumberWithDelayedImport } from '@zocdoc-frontend-common/components-phonenumber/PhoneNumberWithDelayedImport';
import { Textbox } from '@zocdoc-frontend-common/components-textbox';
import { FormRow } from '@zocdoc-frontend-common/styles-grid';
import { blue100 } from '@zocdoc-mezzanine/foundations-colors';
import { labelSmall } from '@zocdoc-mezzanine/foundations-typography';
import AddPhoneBox from '../AddPhoneBox';

export type PhoneInputProps = {
    hasError: boolean;
    onAddOrEditPhoneNumberClick: () => void;
    phoneNumber: string;
    phoneNumberRequired: boolean;
    trackClickAddPhone: () => void;
    trackClickEditPhone: () => void;
    isMfaEnabled: boolean;
};

/**
 * @param {boolean} hasError
 * @param {function(): void} onAddOrEditPhoneNumberClick
 * @param {string} phoneNumber
 * @param {boolean} phoneNumberRequired
 * @param {function(): void} trackClickAddPhone
 * @param {function(): void} trackClickEditPhone
 * @param {boolean} isMfaEnabled
 * @returns {JSX.Element}
 */
const PhoneInputBlockView = ({
    hasError,
    onAddOrEditPhoneNumberClick,
    phoneNumber,
    phoneNumberRequired,
    trackClickAddPhone,
    trackClickEditPhone,
    isMfaEnabled,
}: PhoneInputProps): JSX.Element => {
    return (
        <FormRow aria-labelledby="phone-input-form-label">
            <FormLabel id="phone-input-form-label">
                Phone number where the doctor can reach you
            </FormLabel>
            <PhoneTextbox
                phoneNumberRequired={phoneNumberRequired}
                trackClickAddPhone={trackClickAddPhone}
                onAddOrEditPhoneNumberClick={onAddOrEditPhoneNumberClick}
                hasError={hasError}
                phoneNumber={phoneNumber}
                trackClickEditPhone={trackClickEditPhone}
                isMfaEnabled={isMfaEnabled}
            />
        </FormRow>
    );
};

/**
 * @param {boolean} phoneNumberRequired
 * @param {function(): void} trackClickAddPhone
 * @param {function(): void} onAddOrEditPhoneNumberClick
 * @param {boolean} hasError
 * @param {string} phoneNumber
 * @param {function(): void} trackClickEditPhone
 * @param {boolean} isMfaEnabled
 * @returns {JSX.Element}
 */
const PhoneTextbox = ({
    phoneNumberRequired,
    trackClickAddPhone,
    onAddOrEditPhoneNumberClick,
    hasError,
    phoneNumber,
    trackClickEditPhone,
    isMfaEnabled,
}: PhoneInputProps): JSX.Element => {
    if (isMfaEnabled) {
        return (
            <Textbox
                data-test={'mfa-enabled-text-box'}
                value={phoneNumber}
                disabled={true}
            />
        );
    }

    if (phoneNumberRequired) {
        return (
            <AddPhoneBox
                hasError={hasError}
                onClick={() => {
                    trackClickAddPhone();
                    onAddOrEditPhoneNumberClick();
                }}
            />
        );
    }

    return (
        <PhoneNumberWithDelayedImport
            initialValue={phoneNumber}
            onEdit={() => {
                trackClickEditPhone();
                onAddOrEditPhoneNumberClick();
            }}
            presentationMode
        />
    );
};

const FormLabel = styled.label`
    ${labelSmall};
    color: ${blue100};
    display: inline-block;
    padding-bottom: 4px;
`;

export default PhoneInputBlockView;
